import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import { useLayoutEffect } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { pageVariants } from "./motion/tokens";
import { AccountPage } from "../pages/Account";
import { AdminPage } from "../pages/Admin";
import { ApartmentDetail } from "../pages/ApartmentDetail";
import { Apartments } from "../pages/Apartments";
import { Contact } from "../pages/Contact";
import { Gallery } from "../pages/Gallery";
import { Home } from "../pages/Home";
import { LocalGuide } from "../pages/LocalGuide";
import { NotFound } from "../pages/NotFound";
import { Privacy } from "../pages/Privacy";
import { ConfirmSubscription } from "../pages/ConfirmSubscription";
import { UnsubscribePage } from "../pages/Unsubscribe";

function LegacyAccommodationRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/apartments/${slug ?? ""}`} replace />;
}

/**
 * Animated page transitions for a PERSISTENT layout. Keyed on pathname only
 * (query/hash changes never remount the page, so BookingSearch state survives).
 * The exiting <m.div> keeps the previous <Routes location> mounted until its
 * opacity exit completes, so the old page renders during its fade-out.
 */
export function AnimatedRoutes() {
  const location = useLocation();
  const reduce = useReducedMotion();

  // Scroll management on navigation. Runs synchronously on location change
  // (proven reliable). No hash -> jump to top; hash -> scroll to the anchor
  // once it has mounted (retry covers the exit/enter window).
  useLayoutEffect(() => {
    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      const go = () => {
        const target = document.getElementById(id);
        if (!target) {
          return;
        }
        const headerHeight = document.querySelector(".site-header")?.getBoundingClientRect().height ?? 96;
        const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 18;
        window.scrollTo({ top: Math.max(0, top), left: 0, behavior: "auto" });
      };
      const a = window.setTimeout(go, 80);
      const b = window.setTimeout(go, 450);
      return () => {
        window.clearTimeout(a);
        window.clearTimeout(b);
      };
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    return undefined;
  }, [location.pathname, location.hash]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <m.div
        key={location.pathname}
        className="route-view"
        initial={reduce ? false : "initial"}
        animate={reduce ? undefined : "enter"}
        exit={reduce ? undefined : "exit"}
        variants={reduce ? undefined : pageVariants}
      >
        <Routes location={location}>
          <Route index element={<Home />} />
          <Route path="apartments" element={<Apartments />} />
          <Route path="apartments/:slug" element={<ApartmentDetail />} />
          <Route path="wp-accommodation/:slug" element={<LegacyAccommodationRedirect />} />
          <Route path="gallery" element={<Gallery />} />
          <Route path="local-guide" element={<LocalGuide />} />
          <Route path="contact" element={<Contact />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="confirm-subscription" element={<ConfirmSubscription />} />
          <Route path="unsubscribe" element={<UnsubscribePage />} />
          <Route path="admin" element={<AdminPage />} />
          <Route path="admin/chat-settings" element={<AdminPage />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </m.div>
    </AnimatePresence>
  );
}
